import React, { useState } from "react";
import { Plus, X, Send, BarChart3 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { toast } from "sonner";

/** Admin-only composer for a new poll post. Calls onCreated(post) when done. */
export default function PollComposer({ onCreated }) {
  const { t } = useT();
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [duration, setDuration] = useState("1w");
  const [busy, setBusy] = useState(false);

  const setOption = (i, v) => {
    setOptions((prev) => prev.map((o, j) => (j === i ? v : o)));
  };
  const addOption = () => {
    if (options.length >= 6) return;
    setOptions((prev) => [...prev, ""]);
  };
  const removeOption = (i) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, j) => j !== i));
  };

  const submit = async (e) => {
    e.preventDefault();
    const q = question.trim();
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (q.length < 3) return toast.error(t("err.required"));
    if (opts.length < 2) return toast.error(t("err.required"));
    setBusy(true);
    try {
      const { data } = await api.post("/polls", { question: q, options: opts, duration });
      toast.success(t("poll.posted"));
      setQuestion("");
      setOptions(["", ""]);
      setDuration("1w");
      if (typeof onCreated === "function") onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="poll-composer" className="space-y-3">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-[#7C3AED]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{t("poll.badge")}</span>
      </div>
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">{t("poll.question")}</label>
        <input
          data-testid="poll-question-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={500}
          required
          placeholder={t("poll.questionPh")}
          className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
        />
      </div>

      <div className="space-y-2">
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{t("poll.option")}</label>
        {options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              data-testid={`poll-option-input-${i}`}
              value={o}
              onChange={(e) => setOption(i, e.target.value)}
              maxLength={200}
              placeholder={t("poll.optionPh", { n: i + 1 })}
              className="flex-1 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            />
            {options.length > 2 && (
              <button
                type="button"
                onClick={() => removeOption(i)}
                data-testid={`poll-option-remove-${i}`}
                aria-label={t("poll.removeOption")}
                className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 text-[#71717A] hover:border-[#E11D48] hover:text-[#E11D48] transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {options.length < 6 && (
          <button
            type="button"
            onClick={addOption}
            data-testid="poll-option-add"
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#7C3AED] hover:text-[#6D28D9] transition-colors"
          >
            <Plus className="w-3 h-3" /> {t("poll.addOption")}
          </button>
        )}
      </div>

      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">{t("poll.duration")}</label>
        <div className="flex gap-2 flex-wrap">
          {[
            ["1d", t("poll.dur.1d")],
            ["1w", t("poll.dur.1w")],
            ["never", t("poll.dur.never")],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setDuration(k)}
              data-testid={`poll-duration-${k}`}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                duration === k
                  ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                  : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:border-[#7C3AED] hover:text-[#7C3AED]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="poll-submit-btn"
          className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-5 py-2 hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
        >
          <Send className="w-3 h-3" /> {busy ? t("poll.submitting") : t("poll.submit")}
        </button>
      </div>
    </form>
  );
}
